import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../../providers/AuthProvider';
import {
  Form,
  Button,
  Table,
  Row,
  Col,
  Alert,
  Spinner,
  Badge
} from 'react-bootstrap';
import axios from 'axios';
import { WebSocketContext } from '../../providers/WebSocketProvider';
import DeliveryPlanModal from './OrderPlan.js';
import OrderProductsTable from '../../components/tables/CreateNewOrder/OrderProductsTable.js';
import { CABLE_NAMES_NO_ROPE, CABLE_COLORS } from '../../data/cableProducts.js';
import ReserveFromStockModal from '../../components/sales-management/ReserveFromStockModal.js';
import { API } from '../../config/api';
import { orderBlockedReason } from '../../helpers/orderPlanGate';
import { formatProductLabel } from '../../helpers/productLabel';

const API_ORDERS_URL = `${API.BACKEND}/api/v1/orders`;
const API_STANDARDS_URL = `${API.BACKEND}/api/v1/standard`;
const REDIS_API = API.REDIS;

// eng-backend (:5000) with auth — for warehouse stock lookup + reservation.
const ENG = axios.create({ baseURL: API.BACKEND, timeout: 10000 });
ENG.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});
const REDIS = axios.create({ baseURL: REDIS_API, timeout: 10000 });
REDIS.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});

const productNames = CABLE_NAMES_NO_ROPE;
const colorOptions = CABLE_COLORS;

const toNum = v => {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  return Number.isFinite(n) ? n : '';
};

const OrderCreateForm = () => {
  const { socket } = useContext(WebSocketContext);
  const [authState] = useAuthContext();

  const [createdOrder, setCreatedOrder] = useState(null);
  const [stockAvail, setStockAvail] = useState({}); // "LABEL|COLOR" -> free meters

  // Load available free STOCK once so the "From stock" toggle can show how much of
  // each product is already on the shelf (full / partial / none).
  useEffect(() => {
    ENG.get('/api/v1/warehouse/stock?owner=stock')
      .then(r => {
        const items = r.data?.data || [];
        const map = {};
        for (const it of items) {
          if (it.status && it.status !== 'available') continue;
          const key =
            String(it.cableName || '').toUpperCase() +
            '|' +
            String(it.color || '').toUpperCase();
          map[key] = (map[key] || 0) + (Number(it.lengthMeters) || 0);
        }
        setStockAvail(map);
      })
      .catch(() => {});
  }, []);

  const handleToggleFromStock = index => {
    setFormData(prev => {
      const products = [...(prev.products || [])];
      if (products[index]) {
        products[index] = { ...products[index], fromStock: !products[index].fromStock };
      }
      return { ...prev, products };
    });
  };

  // ---------------- ORDER HEADER ----------------
  const [formData, setFormData] = useState({
    client: '',
    // Internal by default: roughly nine orders in ten are, and the cost of
    // getting this wrong is asymmetric — a Romanian label on an export pallet
    // is a customs problem, an English one at home is merely odd.
    market: 'internal',
    deliveryDays: '',
    deliveryDate: '',
    createdAt: new Date().toISOString().split('T')[0],
    products: []
  });

  // ---------------- PRODUCT DRAFT ----------------
  const [newProduct, setNewProduct] = useState({
    cableName: '',
    numberOfConductors: '',
    section: '',
    hasMixedConductorSections: false,
    greenYellowSection: '',
    color: '',
    totalOrderedLength: '',
    deliveryPlan: { drums: [], coils: [], summary: '', plannedTotal: 0 }
  });

  const [editingIndex, setEditingIndex] = useState(null);

  // UI state
  const [errorMsg, setErrorMsg] = useState('');
  const [checkingStandard, setCheckingStandard] = useState(false);

  // Modal state
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planIndex, setPlanIndex] = useState(null);

  // ---------------- HEADER CHANGE ----------------
  const handleHeaderChange = e => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = { ...prev };

      if (name === 'deliveryDays') {
        updated.deliveryDays = toNum(value);
        const days = Number(updated.deliveryDays);
        if (Number.isFinite(days) && days > 0) {
          const d = new Date(prev.createdAt);
          d.setDate(d.getDate() + days);
          updated.deliveryDate = d.toISOString().split('T')[0];
        } else {
          updated.deliveryDate = '';
        }
        return updated;
      }

      updated[name] = value;
      return updated;
    });
  };

  // ---------------- PRODUCT CHANGE ----------------
  const handleProductChange = (key, value) => {
    setNewProduct(prev => {
      const next = { ...prev };

      if (
        key === 'numberOfConductors' ||
        key === 'section' ||
        key === 'greenYellowSection' ||
        key === 'totalOrderedLength'
      ) {
        next[key] = toNum(value);
        return next;
      }

      // toggle: dacă îl oprești, curățăm GY
      if (key === 'hasMixedConductorSections') {
        next.hasMixedConductorSections = !!value;
        if (!value) next.greenYellowSection = '';
        return next;
      }

      next[key] = value;
      return next;
    });
  };

  const resetProduct = () => {
    setNewProduct({
      cableName: '',
      numberOfConductors: '',
      section: '',
      hasMixedConductorSections: false,
      greenYellowSection: '',
      color: '',
      totalOrderedLength: '',
      deliveryPlan: { drums: [], coils: [], summary: '', plannedTotal: 0 }
    });
    setEditingIndex(null);
    setErrorMsg('');
  };

  // ---------------- STANDARD CHECK ----------------
  const checkStandardExists = async product => {
    const name = String(product.cableName || '').trim();
    const n = Number(product.numberOfConductors);
    const s = Number(product.section);
    const color = String(product.color || '').trim();

    const mixed = !!product.hasMixedConductorSections;
    const gy = mixed ? Number(product.greenYellowSection) : 0;
    try {
      const url =
        `${API_STANDARDS_URL}/exists/` +
        `${encodeURIComponent(name)}/${n}/${s}/${encodeURIComponent(color)}/${mixed ? 1 : 0}/${mixed ? gy : 0}`;
      const res = await axios.get(url);
      return !!res?.data?.exists;
    } catch (err) {
      // aici nu mai ai 404 (returnează exists:false), deci orice eroare e reală
      throw err;
    }
  };

  const sameProductKey = p => {
    const n = Number(p.numberOfConductors);
    const s = Number(p.section);
    const hasMix = !!p.hasMixedConductorSections;
    const gy = hasMix ? Number(p.greenYellowSection) : null;

    return [
      String(p.cableName || '')
        .trim()
        .toUpperCase(),
      String(p.color || '')
        .trim()
        .toUpperCase(),
      Number.isFinite(n) ? n : '',
      Number.isFinite(s) ? s : '',
      hasMix ? 'MIX' : 'SINGLE',
      hasMix && Number.isFinite(gy) ? gy : ''
    ].join('|');
  };

  const handleAddProduct = async () => {
    setErrorMsg('');

    if (!newProduct.cableName) return setErrorMsg('Selectează Cable Name.');
    if (!newProduct.color) return setErrorMsg('Selectează Color.');

    const n = Number(newProduct.numberOfConductors);
    const s = Number(newProduct.section);
    const L = Number(newProduct.totalOrderedLength);

    if (!Number.isFinite(n) || n <= 0)
      return setErrorMsg('Number of Conductors trebuie să fie > 0.');
    if (!Number.isFinite(s) || s <= 0)
      return setErrorMsg('Section trebuie să fie > 0.');
    if (!Number.isFinite(L) || L <= 0)
      return setErrorMsg('Total Ordered Length trebuie să fie > 0.');

    if (newProduct.hasMixedConductorSections) {
      const gy = Number(newProduct.greenYellowSection);
      if (!Number.isFinite(gy) || gy <= 0)
        return setErrorMsg(
          'GY Section trebuie să fie > 0 când toggle este ON.'
        );
    }

    try {
      setCheckingStandard(true);
      const ok = await checkStandardExists(newProduct);
      if (!ok) {
        setErrorMsg(`Missing standard for ${newProduct.cableName} ${n}x${s}.`);
        return;
      }

      const sanitized = {
        cableName: String(newProduct.cableName),
        numberOfConductors: n,
        section: s,
        hasMixedConductorSections: Boolean(
          newProduct.hasMixedConductorSections
        ),
        greenYellowSection: newProduct.hasMixedConductorSections
          ? Number(newProduct.greenYellowSection)
          : null,
        color: String(newProduct.color),
        totalOrderedLength: L,
        deliveryPlan: newProduct.deliveryPlan || {
          drums: [],
          coils: [],
          summary: '',
          plannedTotal: 0
        }
      };

      // ✅ dacă e edit, păstrăm comportamentul de edit
      if (editingIndex !== null) {
        const updated = [...formData.products];
        updated[editingIndex] = sanitized;
        setFormData(prev => ({ ...prev, products: updated }));
        resetProduct();
        return;
      }

      // ✅ dacă NU e edit -> încercăm merge automat
      setFormData(prev => {
        const key = sameProductKey(sanitized);
        const idx = (prev.products || []).findIndex(
          p => sameProductKey(p) === key
        );

        // NU există -> add normal
        if (idx === -1) {
          return { ...prev, products: [...prev.products, sanitized] };
        }

        // există -> merge
        const products = [...prev.products];
        const existing = products[idx];

        const newTotal =
          Number(existing.totalOrderedLength || 0) +
          Number(sanitized.totalOrderedLength || 0);

        // păstrăm planul, dar recalculăm mismatch implicit (plannedTotal rămâne ce era)
        const keptPlan = existing.deliveryPlan || {
          drums: [],
          coils: [],
          summary: '',
          plannedTotal: 0
        };

        products[idx] = {
          ...existing,
          totalOrderedLength: newTotal,
          deliveryPlan: {
            ...keptPlan
            // summary rămâne, plannedTotal rămâne
            // (modalul o să arate mismatch până îl ajustezi)
          }
        };

        return { ...prev, products };
      });

      resetProduct();
    } catch (err) {
      console.error(err);
      setErrorMsg(
        err?.response?.data?.error ||
          err?.message ||
          'Failed checking standard.'
      );
    } finally {
      setCheckingStandard(false);
    }
  };

  const handleEditProduct = index => {
    const p = formData.products[index];
    setNewProduct({
      cableName: p.cableName ?? '',
      numberOfConductors: p.numberOfConductors ?? '',
      section: p.section ?? '',
      hasMixedConductorSections: !!p.hasMixedConductorSections,
      greenYellowSection: p.greenYellowSection ?? '',
      color: p.color ?? '',
      totalOrderedLength: p.totalOrderedLength ?? '',
      deliveryPlan: p.deliveryPlan || {
        drums: [],
        coils: [],
        summary: '',
        plannedTotal: 0
      }
    });
    setEditingIndex(index);
    setErrorMsg('');
  };

  const handleRemoveProduct = index => {
    if (!window.confirm('Delete product?')) return;
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  // ---------------- DELIVERY PLAN MODAL ----------------
  const openPlanModal = index => {
    setPlanIndex(index);
    setPlanModalOpen(true);
  };

  const handleSavePlan = updatedPlan => {
    // updatedPlan: { drums, coils, palletPlan, summary, plannedTotal }
    setFormData(prev => {
      const products = [...prev.products];
      const p = products[planIndex];
      if (!p) return prev;

      products[planIndex] = {
        ...p,
        deliveryPlan: {
          drums: updatedPlan.drums || [],
          coils: updatedPlan.coils || [],
          palletPlan: updatedPlan.palletPlan || [], // was dropped — keep it
          summary: updatedPlan.summary || '',
          plannedTotal: Number(updatedPlan.plannedTotal) || 0
        }
      };

      return { ...prev, products };
    });
  };

  // ---------------- SUBMIT ORDER (TEST) ----------------
  const buildPayload = () => ({
    createdBy:
      authState?.user?.name ||
      authState?.user?.email ||
      authState?.user?.username ||
      'Sales Team',
    client: String(formData.client || '').trim(),
    market: formData.market === 'export' ? 'export' : 'internal',
    deliveryDays: Number(formData.deliveryDays) || 0,
    deliveryDate: formData.deliveryDate || '',
    createdAt: formData.createdAt,
    products: (formData.products || []).map(p => ({
      cableName: p.cableName,
      numberOfConductors: Number(p.numberOfConductors),
      section: Number(p.section),
      hasMixedConductorSections: !!p.hasMixedConductorSections,
      greenYellowSection: p.hasMixedConductorSections
        ? Number(p.greenYellowSection)
        : null,
      color: p.color,
      totalOrderedLength: Number(p.totalOrderedLength),
      deliveryPlan: p.deliveryPlan || {
        drums: [],
        coils: [],
        summary: '',
        plannedTotal: 0
      }
    }))
  });

  // Reserve matching free stock for every "From stock" product (greedy, partial OK).
  // Reuses the reserve endpoints so the backend auto-creates a pick task; the
  // remainder (ordered − reserved) simply stays to-produce.
  const autoReserveFromStock = async (order, products) => {
    const fromStock = (products || []).filter(p => p.fromStock);
    if (!fromStock.length) return;
    for (const line of order.lines || []) {
      const lp = line.product || {};
      const match = fromStock.find(
        p =>
          String(p.cableName || '').toUpperCase() === String(lp.cableName || '').toUpperCase() &&
          Number(p.numberOfConductors) === Number(lp.numberOfConductors) &&
          Number(p.section) === Number(lp.section) &&
          String(p.color || '').toUpperCase() === String(lp.color || '').toUpperCase()
      );
      if (!match) continue;

      const want = Number(match.totalOrderedLength) || Number(line.totalOrderedLength) || 0;
      const label = `${lp.cableName || ''} ${lp.numberOfConductors || ''}x${lp.section || ''}`.trim();
      let items = [];
      try {
        items =
          (await ENG.get(
            `/api/v1/warehouse/stock?owner=stock&cableName=${encodeURIComponent(label)}`
          )).data?.data || [];
      } catch {
        continue;
      }
      const matching = items
        .filter(s => (s.status ? s.status === 'available' : true))
        .filter(
          s => !match.color || String(s.color || '').toUpperCase() === String(match.color).toUpperCase()
        )
        .sort((a, b) => (Number(b.lengthMeters) || 0) - (Number(a.lengthMeters) || 0)); // largest first

      let got = 0;
      const serials = [];
      for (const item of matching) {
        if (got >= want) break;
        try {
          await ENG.post(`/api/v1/warehouse/items/${item.serial}/reserve-sales`, {
            salesOrderId: order.orderId,
            salesOrderCode: order.orderCode,
            customerName: order.client
          });
          got += Number(item.lengthMeters) || 0;
          serials.push(item.serial);
        } catch {
          /* skip this item */
        }
      }
      if (serials.length) {
        try {
          await REDIS.post(`/api/redis/orders/${order.orderId}/reserve-from-stock`, {
            lineId: line.lineId,
            meters: Math.min(got, want),
            serials
          });
        } catch {
          /* ignore recording error */
        }
      }
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMsg('');

    // ── delivery-plan gate: every product must be 100% planned ──
    //
    // Same predicate the button uses, so the refusal cannot disagree with what
    // the screen said a moment earlier. Kept here as well: the button is the
    // courtesy, this is the guarantee.
    const products = formData.products || [];
    const blocked = orderBlockedReason(products);
    if (blocked) {
      setErrorMsg(`Cannot create the order — ${blocked}`);
      return;
    }

    const payload = buildPayload();
    console.log('ORDER PAYLOAD', payload);

    try {
      const res = await axios.post(`${REDIS_API}/api/redis/new-order`, payload);
      const created = res?.data?.order;
      if (created?.orderId) {
        // Auto-reserve matching stock for every "From stock" product (partial OK;
        // the remainder stays to-produce). Creates a pick task per order.
        await autoReserveFromStock(created, products);
        // 3c — modal still opens for any additional manual reservation
        setCreatedOrder(created);
      }
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to create the order.'
      );
    }
  };

  const productPreview = useMemo(() => {
    if (!newProduct.cableName) return null;
    const col = newProduct.color ? `, ${newProduct.color}` : '';
    return `${formatProductLabel(newProduct)}${col}`;
  }, [newProduct]);

  const planStatusBadge = p => {
    const target = Number(p.totalOrderedLength) || 0;
    const planned = Number(p.deliveryPlan?.plannedTotal) || 0;

    if (!target) return <Badge bg="secondary">No length</Badge>;
    if (!planned) return <Badge bg="secondary">No plan</Badge>;
    if (planned === target) return <Badge bg="success">Planned</Badge>;
    return (
      <Badge bg="warning" text="dark">
        Mismatch
      </Badge>
    );
  };

  return (
    <div className="container mt-4">
      <h2>Create New Order</h2>

      <Form onSubmit={handleSubmit}>
        {/* HEADER */}
        <Row className="g-3">
          <Col md={3}>
            <Form.Label>Client</Form.Label>
            <Form.Control
              name="client"
              value={formData.client}
              onChange={handleHeaderChange}
              required
            />
          </Col>

          <Col md={3}>
            {/* Two buttons rather than a switch. A switch has an implicit "off"
                that reads as "not export" without saying what it IS, and this
                decides the language on every label of the shipment. */}
            <Form.Label>Market</Form.Label>
            <div className="btn-group w-100" role="group">
              <button
                type="button"
                className={`btn btn-sm ${formData.market === 'internal' ? 'btn-primary' : 'btn-phoenix-secondary'}`}
                onClick={() => setFormData(f => ({ ...f, market: 'internal' }))}
              >
                Internal
              </button>
              <button
                type="button"
                className={`btn btn-sm ${formData.market === 'export' ? 'btn-primary' : 'btn-phoenix-secondary'}`}
                onClick={() => setFormData(f => ({ ...f, market: 'export' }))}
              >
                Export
              </button>
            </div>
            {formData.market === 'export' && (
              <div className="fs-10 text-warning mt-1">
                Labels print in English, under the international product name.
              </div>
            )}
          </Col>

          <Col md={3}>
            <Form.Label>Delivery Days</Form.Label>
            <Form.Control
              type="number"
              name="deliveryDays"
              value={formData.deliveryDays}
              onChange={handleHeaderChange}
              min={0}
            />
          </Col>

          <Col md={3}>
            <Form.Label>Delivery Date</Form.Label>
            <Form.Control type="date" value={formData.deliveryDate} disabled />
          </Col>

          <Col md={3}>
            <Form.Label>Created At</Form.Label>
            <Form.Control type="date" value={formData.createdAt} disabled />
          </Col>

        </Row>

        {/* PRODUCTS HEADER + TOGGLE */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <h4 className="mb-0">Products (Cables)</h4>

          <Form.Check
            type="switch"
            label="Different Conductor Sections (GY)"
            checked={newProduct.hasMixedConductorSections}
            onChange={e =>
              handleProductChange('hasMixedConductorSections', e.target.checked)
            }
          />
        </div>

        {errorMsg && (
          <Alert variant="danger" className="mt-3 mb-0">
            {errorMsg}
          </Alert>
        )}

        {/* PRODUCT FIELDS */}
        <Row className="g-3 mt-2">
          <Col md={3}>
            <Form.Label>Cable Name</Form.Label>
            <Form.Select
              value={newProduct.cableName}
              onChange={e => handleProductChange('cableName', e.target.value)}
            >
              <option value="">Select cable</option>
              {productNames.map(n => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Number of Conductors</Form.Label>
            <Form.Control
              type="number"
              value={newProduct.numberOfConductors}
              onChange={e =>
                handleProductChange('numberOfConductors', e.target.value)
              }
              min={0}
            />
          </Col>

          <Col md={3}>
            <Form.Label>Section (mm²)</Form.Label>
            <Form.Control
              type="number"
              value={newProduct.section}
              onChange={e => handleProductChange('section', e.target.value)}
              min={0}
              step="0.01"
            />
          </Col>

          {newProduct.hasMixedConductorSections && (
            <Col md={3}>
              <Form.Label>GY Section – Green-Yellow Section (mm²)</Form.Label>
              <Form.Control
                type="number"
                value={newProduct.greenYellowSection}
                onChange={e =>
                  handleProductChange('greenYellowSection', e.target.value)
                }
                min={0}
                step="0.01"
              />
            </Col>
          )}
        </Row>

        <Row className="g-3 mt-2">
          <Col md={3}>
            <Form.Label>Color</Form.Label>
            <Form.Select
              value={newProduct.color}
              onChange={e => handleProductChange('color', e.target.value)}
            >
              <option value="">Select color</option>
              {colorOptions.map(c => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Total Ordered Length (m)</Form.Label>
            <Form.Control
              type="number"
              value={newProduct.totalOrderedLength}
              onChange={e =>
                handleProductChange('totalOrderedLength', e.target.value)
              }
              min={0}
            />
          </Col>
        </Row>

        {productPreview && (
          <div className="mt-3 p-2 border rounded bg-light">
            <strong>Preview:</strong> {productPreview}
          </div>
        )}

        {/* ADD PRODUCT */}
        <div className="mt-3 d-flex justify-content-end gap-2">
          <Button
            variant="success"
            onClick={handleAddProduct}
            disabled={checkingStandard}
          >
            {checkingStandard ? (
              <>
                <Spinner size="sm" className="me-2" /> Checking standard...
              </>
            ) : editingIndex !== null ? (
              'Update Product'
            ) : (
              'Add Product'
            )}
          </Button>
        </div>

        {/* PRODUCTS TABLE */}
        {formData.products.length > 0 && (
          <div className="mt-4">
            <h4 className="mb-3">
              Products in Order{' '}
              <span className="fw-normal text-700">
                ({formData.products.length})
              </span>
            </h4>

            <OrderProductsTable
              products={formData.products}
              openPlanModal={openPlanModal}
              handleEditProduct={handleEditProduct}
              handleRemoveProduct={handleRemoveProduct}
              toggleFromStock={handleToggleFromStock}
              stockAvail={stockAvail}
            />
          </div>
        )}

        {/* CREATE ORDER */}
        {/* The reason sits next to the button. It used to be set on submit and
            rendered ~140 lines of form above it, so clicking Create Order with
            an unplanned product scrolled nothing, showed nothing, and read as a
            dead button. */}
        {(() => {
          const blocked = orderBlockedReason(formData.products || []);
          return (
            <div className="mt-4 d-flex justify-content-end align-items-center gap-3 flex-wrap">
              {blocked && (
                <div className="text-danger fs-9 text-end" style={{ maxWidth: 560 }}>
                  {blocked}
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                disabled={!!blocked}
                title={blocked || 'Create the order'}
              >
                Create Order
              </Button>
            </div>
          );
        })()}
      </Form>

      {/* DELIVERY PLAN MODAL */}
      <DeliveryPlanModal
        show={planModalOpen}
        onHide={() => setPlanModalOpen(false)}
        product={planIndex !== null ? formData.products[planIndex] : null}
        onSave={handleSavePlan}
      />

      {/* 3c — reserve matching finished goods from stock after the order is created */}
      <ReserveFromStockModal
        order={createdOrder}
        show={!!createdOrder}
        onHide={() => setCreatedOrder(null)}
      />
    </div>
  );
};

export default OrderCreateForm;
